"use client";

import { useState, useMemo, useRef } from "react";
import { Country, City } from "country-state-city";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import { ContactPhoneIti, type ContactPhoneItiHandle } from "./ContactPhoneIti";
import { SearchableSelect, type SelectOption } from "@/components/ui/SearchableSelect";

// ─────────────────────────────────────────────────────────────────────────────

const ACCENT = "#2BA6E6";

import { SUBMIT_LEAD_API_URL } from "@/lib/rem-api";

// All countries as SelectOptions, sorted A-Z
const ALL_COUNTRIES: SelectOption[] = Country.getAllCountries()
  .sort((a, b) => a.name.localeCompare(b.name))
  .map((c) => ({ value: c.isoCode, label: c.name, flag: c.flag }));

const SUBJECT_OPTIONS: SelectOption[] = [
  { value: "General Query", label: "General Query" },
  { value: "Suggestions & Feedback", label: "Suggestions & Feedback" },
  { value: "Technical Support", label: "Technical Support" },
];

interface FormState {
  name: string;
  email: string;
  company: string;
  countryCode: string; // ISO 3166-1 alpha-2, e.g. "AE"
  city: string;
  subject: string;
  message: string;
}

const EMPTY: FormState = {
  name: "",
  email: "",
  company: "",
  countryCode: "",
  city: "",
  subject: "",
  message: "",
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.55, delay, ease: "easeOut" as const },
});

export function ContactForm() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Partial<FormState>>({});
  const [contactPhoneError, setContactPhoneError] = useState<string | null>(null);
  const phoneRef = useRef<ContactPhoneItiHandle>(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // City options for the selected country, re-computed only when countryCode changes
    const cityOptions = useMemo<SelectOption[]>(
        () =>
            form.countryCode
                ? [
                    ...new Map(
                        (City.getCitiesOfCountry(form.countryCode) ?? [])
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((c) => [c.name, { value: c.name, label: c.name }])
                    ).values(),
                ]
                : [],
        [form.countryCode]
    );

  const validate = (): boolean => {
    const e: Partial<FormState> = {};
    if (!form.name.trim()) e.name = "Name is required.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email address.";
    if (!form.subject.trim()) e.subject = "Subject is required.";
    if (!form.message.trim()) e.message = "Message is required.";
    setErrors(e);

    setContactPhoneError(null);
    const phoneOk = phoneRef.current?.validate() ?? false;
    if (!phoneOk) {
      const digits = phoneRef.current?.getMobileDigits() ?? "";
      setContactPhoneError(
        !digits.length ? "Contact number is required." : "Please enter a valid phone number."
      );
    }

    return Object.keys(e).length === 0 && phoneOk;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      // Reset city whenever country changes
      ...(name === "countryCode" ? { city: "" } : {}),
    }));
    if (errors[name as keyof FormState]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (apiError) setApiError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    try {
      const payload: Record<string, string> = {
        source: "website",
        contact_name: form.name.trim(),
        email: form.email.trim(),
        notes: `Subject: ${form.subject}\n\n${form.message.trim()}`,
      };
      if (form.company.trim()) payload.company_name = form.company.trim();
      if (form.countryCode) {
        const countryName = Country.getCountryByCode(form.countryCode)?.name ?? form.countryCode;
        payload.country = countryName;
      }
      if (form.city.trim()) payload.city = form.city.trim();

      const mobileDigits = phoneRef.current?.getMobileDigits() ?? "";
      if (mobileDigits) payload.mobile = mobileDigits;

      const res = await fetch(SUBMIT_LEAD_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `Server responded with ${res.status}`);
      }

      setSubmitted(true);
      setForm(EMPTY);
      phoneRef.current?.clear();
      setContactPhoneError(null);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Something went wrong. Please try again.";
      setApiError(message);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field: keyof FormState) =>
    `w-full px-4 py-3 rounded-xl border text-sm text-neutral-900 placeholder-neutral-400 outline-none transition-all duration-200 bg-white focus:ring-2 ${
      errors[field]
        ? "border-red-400 focus:ring-red-100"
        : "border-neutral-200 focus:border-[#2BA6E6] focus:ring-blue-50"
    }`;

  return (
    <motion.div {...fadeUp(0.1)} className="lg:col-span-3">
      {submitted ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="h-full flex flex-col items-center justify-center text-center py-16 px-8 rounded-2xl border border-neutral-100 bg-neutral-50"
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
            style={{ backgroundColor: "rgba(43,166,230,0.1)" }}
          >
            <CheckCircle2 className="w-8 h-8" style={{ color: ACCENT }} />
          </div>
          <h3 className="text-xl font-bold text-neutral-900 mb-2">Message Sent!</h3>
          <p className="text-neutral-600 mb-6 max-w-xs">
              Thank you! Your feedback has been received. The RealtorsIn team will review it and get back to you soon.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-sm font-semibold transition-colors"
            style={{ color: ACCENT }}
          >
            Send another message
          </button>
        </motion.div>
      ) : (
        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-white rounded-2xl border border-neutral-100 shadow-sm p-6 md:p-8 space-y-5"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
                Full Name *
              </label>
              <input
                name="name"
                type="text"
                placeholder="Your full name"
                value={form.name}
                onChange={handleChange}
                className={inputClass("name")}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
                Email Address *
              </label>
              <input
                name="email"
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={handleChange}
                className={inputClass("email")}
              />
              {errors.email && (
                <p className="text-xs text-red-500 mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
              Company
            </label>
            <input
              name="company"
              type="text"
              placeholder="Your company or agency name (optional)"
              value={form.company}
              onChange={handleChange}
              className={inputClass("company")}
            />
          </div>

          <div>
            <label
              htmlFor="floating_lead_phone"
              className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide"
            >
              Contact{" "}
              <span className="text-red-500" aria-hidden>
                *
              </span>
            </label>
            <ContactPhoneIti
              ref={phoneRef}
              hasError={!!contactPhoneError}
              onChange={() => {
                if (contactPhoneError) setContactPhoneError(null);
                if (apiError) setApiError(null);
              }}
            />
            {contactPhoneError && (
              <p className="text-xs text-red-500 mt-1">{contactPhoneError}</p>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {/* Country — full world list with flag + search */}
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
                Country
              </label>
              <SearchableSelect
                options={ALL_COUNTRIES}
                value={form.countryCode}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, countryCode: val, city: "" }));
                  if (apiError) setApiError(null);
                }}
                placeholder="Select country"
                searchPlaceholder="Search countries…"
              />
            </div>

            {/* City — auto-populated from selected country, with search */}
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
                City
              </label>
              <SearchableSelect
                options={cityOptions}
                value={form.city}
                onChange={(val) => {
                  setForm((prev) => ({ ...prev, city: val }));
                  if (apiError) setApiError(null);
                }}
                placeholder={
                  !form.countryCode
                    ? "Select country first"
                    : cityOptions.length === 0
                    ? "No cities available"
                    : "Select city"
                }
                searchPlaceholder="Search cities…"
                disabled={!form.countryCode || cityOptions.length === 0}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
              Subject *
            </label>
            <SearchableSelect
              options={SUBJECT_OPTIONS}
              value={form.subject}
              onChange={(val) => {
                setForm((prev) => ({ ...prev, subject: val }));
                if (errors.subject) {
                  setErrors((prev) => ({ ...prev, subject: undefined }));
                }
                if (apiError) setApiError(null);
              }}
              placeholder="Select a subject"
              searchable={false}
              hasError={!!errors.subject}
            />
            {errors.subject && (
              <p className="text-xs text-red-500 mt-1">{errors.subject}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1.5 uppercase tracking-wide">
              Message *
            </label>
            <textarea
              name="message"
              rows={5}
              placeholder="Tell us how we can help..."
              value={form.message}
              onChange={handleChange}
              className={`${inputClass("message")} resize-none`}
            />
            {errors.message && (
              <p className="text-xs text-red-500 mt-1">{errors.message}</p>
            )}
          </div>

          {apiError && (
            <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
              {apiError}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl text-white font-semibold text-sm shadow-lg hover:opacity-90 active:scale-[0.99] transition-all duration-200 disabled:opacity-60"
            style={{ backgroundColor: ACCENT }}
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Send Message
              </>
            )}
          </button>
        </form>
      )}
    </motion.div>
  );
}
